import { useState, useEffect } from 'react'
import { Prescription } from '../types/prescription'
import { Patient } from '../types/patient'
import { User } from '../types/user'
import { ContactInfo, PracticeLocation } from '../types/practiceLocation'
import { getAgeDisplay, isUnder12 } from '../utils/ageUtils'
import { formatDate } from '../utils/locale'
import { userService } from '../services/userService'
import './PrescriptionPrintView.css'

interface PrescriptionPrintViewProps {
  prescription: Prescription
  patient: Patient
  prescriber: User
  practiceLocation?: PracticeLocation
  /** Tenant practice logo (tenant settings); falls back to default asset when header is shown */
  logoUrl?: string | null
}

function getPracticeContactLines(contacts: ContactInfo[] | undefined): string[] {
  if (!contacts?.length) return []
  const typeOrder: Record<ContactInfo['type'], number> = {
    telephone: 0,
    mobile: 1,
    email: 2,
    fax: 3,
  }
  const sorted = [...contacts].sort((a, b) => {
    if (a.isPrimary !== b.isPrimary) return a.isPrimary ? -1 : 1
    return typeOrder[a.type] - typeOrder[b.type]
  })
  const lines: string[] = []
  for (const c of sorted) {
    const label =
      c.type === 'telephone' ? 'Tel' : c.type === 'mobile' ? 'Mobile' : c.type === 'email' ? 'Email' : 'Fax'
    const suffix = c.label ? ` (${c.label})` : ''
    lines.push(`${label}${suffix}: ${c.value}`)
  }
  return lines
}

export function PrescriptionPrintView({
  prescription,
  patient,
  prescriber,
  practiceLocation,
  logoUrl,
}: PrescriptionPrintViewProps) {
  // Get signature URL from prescriber object if available, otherwise fetch it
  const [signatureUrl, setSignatureUrl] = useState<string | null>(
    (prescriber as any).signatureUrl || null
  )
  
  // Load signature from S3 if signatureS3Key exists and URL not already provided
  useEffect(() => {
    // If signatureUrl was already provided (from print window), use it
    if ((prescriber as any).signatureUrl) {
      setSignatureUrl((prescriber as any).signatureUrl)
      return
    }
    
    // Otherwise, fetch it if signatureS3Key exists
    if (prescriber.signatureS3Key && prescriber.id) {
      userService.getSignatureUrl(prescriber.id)
        .then(url => setSignatureUrl(url))
        .catch(err => {
          console.warn('Failed to load signature:', err)
          setSignatureUrl(null)
        })
    } else {
      setSignatureUrl(null)
    }
  }, [prescriber.signatureS3Key, prescriber.id, (prescriber as any).signatureUrl])
  
  const patientAge = getAgeDisplay(patient.dateOfBirth)
  const showAgeInMonths = isUnder12(patient.dateOfBirth)
  const prescriptionDate = formatDate(prescription.prescriptionDate)
  const displayGmsNumber =
    (patient.gmsNumber && patient.gmsNumber.trim()) ||
    (patient.coverage?.gms?.number && String(patient.coverage.gms.number).trim()) ||
    ''
  const isGmsPrescription = prescription.prescriptionType === 'gms'
  const showPatientGmsRow = isGmsPrescription || Boolean(displayGmsNumber)
  const patientGmsPrintValue = displayGmsNumber || (isGmsPrescription ? '—' : '')
  const address = patient.address
    ? `${patient.address.street || ''}, ${patient.address.city || ''}, ${patient.address.postalCode || ''}`.trim()
    : ''

  // Get primary practice address
  const primaryAddress = practiceLocation?.addresses?.find((addr) => addr.type === 'primary' && addr.isActive)
  const practiceAddress = primaryAddress
    ? [
        primaryAddress.addressLine1,
        primaryAddress.addressLine2,
        primaryAddress.city,
        primaryAddress.county,
        primaryAddress.postcode,
      ]
        .filter(Boolean)
        .join(', ')
    : ''

  const practiceContactLines = getPracticeContactLines(practiceLocation?.contacts)
  const showPracticeHeader = Boolean(practiceLocation) || Boolean((logoUrl ?? '').trim())
  const displayLogoUrl = (logoUrl ?? '').trim() || '/logo@0.5x.png'
  const hasPracticeText = Boolean(
    (practiceLocation?.practiceName && practiceLocation.practiceName.trim()) ||
      practiceAddress ||
      practiceContactLines.length,
  )

  const displayMedicalCouncilNumber =
    (prescriber.professionalBodyRegistrationNumber &&
      prescriber.professionalBodyRegistrationNumber.trim()) ||
    (prescription.prescriberMedicalCouncilNumber && prescription.prescriberMedicalCouncilNumber.trim()) ||
    ''

  return (
    <div className="prescription-print-view">
      {showPracticeHeader && (
        <div
          className={`practice-print-header${hasPracticeText ? '' : ' practice-print-header--logo-only'}`}
        >
          {hasPracticeText && (
            <div className="practice-header-text">
              {practiceLocation?.practiceName && (
                <div className="practice-name">{practiceLocation.practiceName}</div>
              )}
              {practiceAddress && <div className="practice-address">{practiceAddress}</div>}
              {practiceContactLines.map((line, i) => (
                <div key={i} className="practice-contact">
                  {line}
                </div>
              ))}
            </div>
          )}
          <div className="practice-logo-container">
            <img src={displayLogoUrl} alt="Practice logo" className="practice-logo" />
          </div>
        </div>
      )}

      <div className="prescription-header">
        <div className="prescription-header-left">
          <h1 className="prescription-title">
            {prescription.prescriptionType.toUpperCase()} PRESCRIPTION
            {prescription.scriptType === 'repeat' && ' - REPEAT'}
          </h1>
        </div>
        <div className="prescription-header-right">
          <div className="prescription-date">Date: {prescriptionDate}</div>
        </div>
      </div>

      <div className="prescription-body">
        {/* Patient Information */}
        <div className="prescription-section patient-section">
          <h2 className="section-title">Patient Information</h2>
          <div className="patient-info-grid">
            <div className="info-item">
              <span className="info-label">Name:</span>
              <span className="info-value">
                {patient.firstName} {patient.lastName}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">Date of Birth:</span>
              <span className="info-value">
                {formatDate(patient.dateOfBirth)}
              </span>
            </div>
            {patient.ppsNumber && (
              <div className="info-item">
                <span className="info-label">PPS Number:</span>
                <span className="info-value">{patient.ppsNumber}</span>
              </div>
            )}
            {showAgeInMonths && (
              <div className="info-item">
                <span className="info-label">Age:</span>
                <span className="info-value">{patientAge}</span>
              </div>
            )}
            {showPatientGmsRow && (
              <div className="info-item">
                <span className="info-label">GMS Number:</span>
                <span className="info-value">{patientGmsPrintValue}</span>
              </div>
            )}
            {address && (
              <div className="info-item full-width">
                <span className="info-label">Address:</span>
                <span className="info-value">{address}</span>
              </div>
            )}
          </div>
        </div>

        {/* Medications */}
        <div className="prescription-section medications-section">
          <h2 className="section-title">Medications</h2>
          <table className="medications-table">
            <thead>
              <tr>
                <th className="med-number">#</th>
                <th className="med-name">Medication</th>
                <th className="med-dose">Dose</th>
                <th className="med-route">Route</th>
                <th className="med-frequency">Frequency</th>
                <th className="med-quantity">Quantity</th>
              </tr>
            </thead>
            <tbody>
              {prescription.medications.map((med, idx) => (
                <tr key={med.medicationId}>
                  <td className="med-number">{idx + 1}</td>
                  <td className="med-name">
                    <div className="med-name-primary">{med.name}</div>
                    {med.gmsCode && (
                      <div className="med-gms-code">GMS: {med.gmsCode}</div>
                    )}
                    {med.instructions && (
                      <div className="med-instructions">{med.instructions}</div>
                    )}
                    {med.indication && (
                      <div className="med-indication">For: {med.indication}</div>
                    )}
                  </td>
                  <td className="med-dose">{med.dose || '-'}</td>
                  <td className="med-route">{med.route || '-'}</td>
                  <td className="med-frequency">{med.frequency || '-'}</td>
                  <td className="med-quantity">
                    {med.quantity.value} {med.quantity.unit}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="prescription-section handwritten-instructions-section">
          <h2 className="section-title">Instructions</h2>
          <div className="handwritten-instructions-box">
            {prescription.isHandwritten && prescription.handwrittenNotes ? (
              <div className="handwritten-instructions-prefill">{prescription.handwrittenNotes}</div>
            ) : null}
            <div className="handwritten-instructions-lines" aria-hidden />
          </div>
        </div>

        {/* General Instructions */}
        {prescription.instructions && (
          <div className="prescription-section instructions-section">
            <h2 className="section-title">Additional instructions</h2>
            <div className="instructions-content">{prescription.instructions}</div>
          </div>
        )}

        {/* Repeat Prescription Information */}
        {prescription.scriptType === 'repeat' && (
          <div className="prescription-section repeat-section">
            <h2 className="section-title">Repeat Prescription Details</h2>
            <div className="repeat-info-grid">
              {prescription.maxRepeats !== undefined && (
                <div className="info-item">
                  <span className="info-label">Maximum Repeats:</span>
                  <span className="info-value">{prescription.maxRepeats}</span>
                </div>
              )}
              <div className="info-item">
                <span className="info-label">Current Repeat:</span>
                <span className="info-value">
                  {prescription.currentRepeatCount}
                  {prescription.maxRepeats !== undefined &&
                    ` of ${prescription.maxRepeats}`}
                </span>
              </div>
              {prescription.reviewDate && (
                <div className="info-item">
                  <span className="info-label">Review Date:</span>
                  <span className="info-value">
                    {formatDate(prescription.reviewDate)}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Prescriber Information and Signature */}
        <div className="prescription-section prescriber-section">
          <div className="prescriber-signature-block">
            <div className="prescriber-signature-area">
              {signatureUrl ? (
                <div className="signature-image-container">
                  <img 
                    src={signatureUrl} 
                    alt="Prescriber signature" 
                    className="signature-image-display"
                    onError={(e) => {
                      console.error('Failed to load signature image from S3')
                      e.currentTarget.style.display = 'none'
                      setSignatureUrl(null)
                    }}
                  />
                  <div className="signature-label">Prescriber Signature</div>
                </div>
              ) : (
                <>
                  <div className="signature-line"></div>
                  <div className="signature-label">Prescriber Signature</div>
                </>
              )}
            </div>
            <div className="prescriber-printed-details">
              <div className="prescriber-name">
                <strong>
                  {prescriber.salutation ? `${prescriber.salutation} ` : ''}
                  {prescriber.firstName} {prescriber.lastName}
                </strong>
              </div>
              {displayMedicalCouncilNumber && (
                <div className="prescriber-imc">
                  Irish Medical Council (IMC) number: {displayMedicalCouncilNumber}
                </div>
              )}
              {prescriber.gmsNumber && (
                <div className="prescriber-gms">GMS Number: {prescriber.gmsNumber}</div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="prescription-footer">
        <div className="footer-note">
          This prescription was generated electronically on {formatDate(new Date().toISOString())}
        </div>
        {prescription.printedAt && (
          <div className="footer-note">
            Printed: {formatDate(prescription.printedAt)}
          </div>
        )}
      </div>
    </div>
  )
}

